import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { Link, useLocation } from 'react-router';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  SurfaceInspectionScope,
  type SurfaceDetailDefinition,
  type SurfaceInspectionContextValue,
} from '@helios/ui';
import {
  sessionScopeKey,
  useSessionScopeFence,
  type SessionScopeIdentity,
} from '@/hooks/useSessionScope';

import {
  snapshotDataSurface,
  type SurfaceSnapshot,
  type SurfaceTable,
} from './dataSurfaceSnapshot';

const SURFACE = '[data-data-surface]';
const CONTROL =
  'button, a, input, select, textarea, label, summary, details, tr, [role="row"], [role="button"], [role="link"], [role="tab"], [role="checkbox"], [role="switch"], [contenteditable="true"], [draggable="true"], svg, canvas';
interface OpenSurface {
  source: HTMLElement;
  snapshot: SurfaceSnapshot;
  fingerprint: string;
  identity: SessionScopeIdentity;
  identityKey: string;
  routeKey: string;
}

function eligible(source: HTMLElement, boundary?: HTMLElement | null) {
  if (
    !source.isConnected ||
    source.matches('button, a, form, [role="button"], [role="link"], [data-native-inspection]') ||
    source.closest('form, [contenteditable="true"], [data-native-modal-boundary]') ||
    source.querySelector(
      'form, [contenteditable="true"], textarea, select, input:not([type="hidden"]):not([type="search"]):not([readonly]):not([disabled])',
    )
  )
    return false;
  // Radix hides the app behind an open modal. Ignore that outer aria-hidden;
  // a tab/source hidden inside our own boundary still invalidates its snapshot.
  for (
    let element: HTMLElement | null = source;
    element && element !== boundary;
    element = element.parentElement
  ) {
    if (element.matches('[hidden], [aria-busy="true"]')) return false;
    // hideOthers may hide an inner AppShell branch to keep its live announcer
    // available. Its own marker distinguishes that modal-only ancestor hiding
    // from an application hiding the source or switching its section.
    const modalAncestor =
      boundary && element !== source && element.getAttribute('data-aria-hidden') === 'true';
    if (element.getAttribute('aria-hidden') === 'true' && !modalAncestor) return false;
    const style = window.getComputedStyle(element);
    if (
      style.display === 'none' ||
      style.visibility === 'hidden' ||
      style.visibility === 'collapse' ||
      style.contentVisibility === 'hidden'
    )
      return false;
  }
  return true;
}

/**
 * Existing native drills win. Unbound data surfaces expand their current rendered
 * data and source links, while an explicit typed supplement can expose more of
 * the same already-loaded authorized record. No synthetic records are added.
 */
export function DataSurfaceProvider({ children }: { children: ReactNode }) {
  const scope = useSessionScopeFence();
  const location = useLocation();
  const routeKey = `${location.pathname}${location.search}${location.hash}`;
  const identityKey = JSON.stringify(sessionScopeKey(scope.identity));
  const [open, setOpen] = useState<OpenSurface | null>(null);
  const root = useRef<HTMLDivElement>(null);
  const registry = useRef(new WeakMap<HTMLElement, SurfaceDetailDefinition>());
  const registrations = useRef(new WeakMap<HTMLElement, object>());
  const active = useRef(open);
  const lastOpened = useRef<OpenSurface | null>(null);
  active.current = open;
  const current = useRef({ scope, routeKey, identityKey });
  current.current = { scope, routeKey, identityKey };
  const inspect = useCallback((source: HTMLElement) => {
    const state = current.current;
    if (
      !root.current?.contains(source) ||
      !state.scope.enabled ||
      !state.scope.isCurrent(state.scope.identity) ||
      !eligible(source)
    )
      return;
    const snapshot = snapshotDataSurface(source, registry.current.get(source));
    const next = {
      source,
      snapshot,
      fingerprint: JSON.stringify(snapshot),
      identity: state.scope.identity,
      identityKey: state.identityKey,
      routeKey: state.routeKey,
    };
    lastOpened.current = next;
    setOpen(next);
  }, []);
  const register = useCallback<SurfaceInspectionContextValue['register']>((source, detail) => {
    const registration = {};
    registrations.current.set(source, registration);
    registry.current.set(source, detail);
    const opened = active.current;
    if (
      opened?.source === source &&
      JSON.stringify(snapshotDataSurface(source, detail)) !== opened.fingerprint
    )
      setOpen(null);
    return () => {
      // React may replace a memo/effect with semantically identical table arrays.
      // Let its synchronous replacement register before treating cleanup as a
      // removed data source. Actual changed data is checked above immediately.
      queueMicrotask(() => {
        if (registrations.current.get(source) !== registration) return;
        registrations.current.delete(source);
        registry.current.delete(source);
        if (active.current?.source === source) setOpen(null);
      });
    };
  }, []);
  const value = useMemo(() => ({ inspect, register }), [inspect, register]);
  const safeOpen =
    open &&
    scope.enabled &&
    open.identityKey === identityKey &&
    open.routeKey === routeKey &&
    scope.isCurrent(open.identity) &&
    eligible(open.source, root.current)
      ? open
      : null;

  useEffect(() => {
    if (open && !safeOpen) setOpen(null);
  }, [open, safeOpen]);

  useEffect(() => {
    if (!open || !root.current) return;
    const observer = new MutationObserver((records) => {
      // Cursor-light coordinates use inline custom properties at pointer speed;
      // they cannot alter source visibility or data and do not invalidate a view.
      if (
        records.every(
          (record) =>
            record.type === 'attributes' &&
            record.attributeName === 'style' &&
            !/(?:^|;)\s*(?:display|visibility|content-visibility)\s*:/i.test(
              `${record.oldValue ?? ''};${(record.target as Element).getAttribute('style') ?? ''}`,
            ),
        )
      )
        return;
      if (
        !open.source.isConnected ||
        !root.current?.contains(open.source) ||
        !current.current.scope.isCurrent(open.identity)
      ) {
        setOpen(null);
        return;
      }
      const touchesSource = records.some((record) => open.source.contains(record.target));
      const changesAncestor = records.some((record) => {
        if (record.type === 'attributes')
          return record.target instanceof Element && record.target.contains(open.source);
        if (record.type !== 'childList') return false;
        return [...record.addedNodes, ...record.removedNodes].some(
          (node) => node === open.source || node.contains(open.source),
        );
      });
      // A notification/realtime update in a sibling pane cannot change this
      // source's rendered records. Do not rescan its tables and computed styles.
      if (!touchesSource && !changesAncestor) return;
      if (
        !eligible(open.source, root.current) ||
        JSON.stringify(snapshotDataSurface(open.source, registry.current.get(open.source))) !==
          open.fingerprint
      ) {
        setOpen(null);
      }
    });
    observer.observe(root.current, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeOldValue: true,
      attributeFilter: [
        'hidden',
        'aria-hidden',
        'data-aria-hidden',
        'aria-busy',
        'data-source-state',
        'class',
        'style',
        'disabled',
        'readonly',
        'type',
        'contenteditable',
        'role',
        'data-native-inspection',
        'data-native-modal-boundary',
      ],
    });
    // A workspace edit lock can sit outside this provider's DOM boundary. Its
    // contenteditable state affects eligibility; unrelated ancestor styles do not.
    for (let ancestor = root.current.parentElement; ancestor; ancestor = ancestor.parentElement) {
      observer.observe(ancestor, {
        attributes: true,
        attributeFilter: ['contenteditable', 'data-native-modal-boundary'],
      });
    }
    return () => observer.disconnect();
  }, [open]);

  function backgroundClick(event: MouseEvent<HTMLDivElement>) {
    if (event.defaultPrevented || event.button !== 0 || !(event.target instanceof Element)) return;
    if (window.getSelection()?.toString()) return;
    const source = event.target.closest<HTMLElement>(SURFACE);
    // Record rows retain their own native open/select behavior. Use a panel's
    // explicit expand control (or its non-table background) for the table view.
    if (
      !source ||
      !root.current?.contains(source) ||
      event.target.closest(CONTROL) ||
      !eligible(source)
    )
      return;
    inspect(source);
  }

  return (
    <SurfaceInspectionScope value={scope.enabled ? value : null}>
      <div ref={root} className="contents" onClick={backgroundClick}>
        {children}
      </div>
      <Dialog
        open={Boolean(safeOpen)}
        onOpenChange={(next) => {
          if (!next) setOpen(null);
        }}
      >
        <DialogContent
          className="flex max-h-[calc(100dvh-2rem)] max-w-5xl flex-col gap-3 overflow-hidden"
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            const previous = lastOpened.current;
            if (
              previous &&
              current.current.scope.isCurrent(previous.identity) &&
              root.current?.contains(previous.source) &&
              eligible(previous.source, root.current)
            ) {
              previous.source
                .querySelector<HTMLElement>('[data-surface-inspect-control]')
                ?.focus({ preventScroll: true });
            }
          }}
        >
          {safeOpen ? (
            <SurfaceSnapshotContent snapshot={safeOpen.snapshot} close={() => setOpen(null)} />
          ) : null}
        </DialogContent>
      </Dialog>
    </SurfaceInspectionScope>
  );
}

function SurfaceSnapshotContent({
  snapshot,
  close,
}: {
  snapshot: SurfaceSnapshot;
  close: () => void;
}) {
  return (
    <>
      <DialogHeader className="mb-0 shrink-0 pr-8">
        <DialogTitle>{snapshot.title}</DialogTitle>
        <DialogDescription>
          Expanded current view. Only loaded, permitted data is shown; use a source link for its
          full register and record history.
        </DialogDescription>
      </DialogHeader>
      <div className="min-h-0 space-y-4 overflow-auto pr-1 text-sm">
        {snapshot.detail?.description ? (
          <p className="text-muted-foreground">{snapshot.detail.description}</p>
        ) : null}
        {snapshot.detail?.sections?.map((section, index) => (
          <section key={`${section.title}-${index}`} className="space-y-2">
            <h3 className="hx-section-title text-sm font-semibold">{section.title}</h3>
            {section.description ? (
              <p className="text-muted-foreground text-xs">{section.description}</p>
            ) : null}
            {section.fields?.length ? (
              <dl className="grid gap-x-5 gap-y-2 sm:grid-cols-2">
                {section.fields.map((field, fieldIndex) => (
                  <div
                    key={`${field.label}-${fieldIndex}`}
                    className="border-border flex justify-between gap-4 border-b py-1"
                  >
                    <dt className="text-muted-foreground text-xs">{field.label}</dt>
                    <dd className="text-right text-xs tabular-nums">
                      {field.value ?? 'Unavailable'}
                    </dd>
                  </div>
                ))}
              </dl>
            ) : null}
            {section.columns?.length && section.rows ? (
              <DetailTable
                caption={section.title}
                columns={[...section.columns]}
                rows={section.rows.map((row) =>
                  row.map((value) => (value === null ? 'Unavailable' : String(value))),
                )}
                omittedRows={0}
              />
            ) : null}
          </section>
        ))}
        {snapshot.context.length ? (
          <section className="space-y-2" aria-label="Displayed context">
            <h3 className="hx-section-title text-sm font-semibold">Displayed context</h3>
            {snapshot.context.map((text, index) => (
              <p key={index} className="break-words text-xs leading-relaxed">
                {text}
              </p>
            ))}
          </section>
        ) : null}
        {snapshot.tables.map((table, index) => (
          <DetailTable key={index} {...table} />
        ))}
        {snapshot.links.length ? (
          <section className="border-border border-t pt-3" aria-label="Source views">
            <h3 className="hx-section-title mb-2 text-sm font-semibold">Source views</h3>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {snapshot.links.map((link) =>
                link.href.startsWith('/') ? (
                  <Link
                    key={`${link.href}-${link.label}`}
                    to={link.href}
                    onClick={close}
                    className="text-context-primary text-xs underline underline-offset-4"
                  >
                    {link.label}
                  </Link>
                ) : (
                  <a
                    key={`${link.href}-${link.label}`}
                    href={link.href}
                    target="_blank"
                    rel="noreferrer"
                    className="text-context-primary text-xs underline underline-offset-4"
                  >
                    {link.label}
                  </a>
                ),
              )}
            </div>
          </section>
        ) : null}
      </div>
    </>
  );
}

function DetailTable({ caption, columns, rows, omittedRows }: SurfaceTable) {
  return (
    <div className="border-border min-w-0 overflow-x-auto rounded-md border">
      <table className="w-full border-collapse text-left text-xs">
        {caption ? (
          <caption className="hx-section-title p-2 text-left text-xs font-semibold">
            {caption}
          </caption>
        ) : null}
        {columns.length ? (
          <thead className="hx-section-header">
            <tr>
              {columns.map((column, index) => (
                <th key={index} scope="col" className="px-3 py-2 font-medium">
                  {column}
                </th>
              ))}
            </tr>
          </thead>
        ) : null}
        <tbody>
          {rows.map((row, index) => (
            <tr key={index} className="border-border border-t">
              {row.map((cell, cellIndex) => (
                <td key={cellIndex} className="break-words px-3 py-2 tabular-nums">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {omittedRows ? (
        <p className="text-muted-foreground border-border border-t p-2 text-xs">
          {omittedRows} additional loaded rows remain in the original view. Use its source register
          to review the full cohort.
        </p>
      ) : null}
    </div>
  );
}
